<script setup>
/* 术语悬浮解释：全应用只此一个实例。
 *
 * 为什么用事件委托而不是每个术语一个 el-tooltip
 * ---------------------------------------------
 * 界面上术语有几百处，每处挂一个 Element Plus tooltip 就是几百个组件实例
 * 加几百个 popper —— 这是个 2 秒轮询的实时控制台，每轮 diff 都要背这些。
 * 委托只需一个监听器、一个浮层节点，数量再多都不变。
 *
 * 触发方式覆盖三种输入
 * -------------------
 * 悬停   鼠标
 * 聚焦   键盘 Tab（术语节点带 tabindex）
 * 点击   触屏（没有 hover，靠点按切换）
 *
 * 离开时延迟 120ms 再收，是为了让指针能从术语移到浮层上 —— 浮层里的
 * 数值经常要复制出来对账，收得太快就选不中。
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { GLOSSARY, SCOPE_LABEL } from '@/glossary'
import { startAutoScan, stopAutoScan } from '@/utils/glossaryScan'

const HIDE_DELAY = 120
const GAP = 8 // 浮层与术语之间的空隙（px）
const EDGE = 8 // 距视口边缘的最小留白（px）

const entry = ref(null)
const pos = ref({ top: 0, left: 0, placement: 'bottom' })
const visible = ref(false)
const pinned = ref(false) // 点击触发的，不随鼠标移开自动收

const tip = ref(null)
let hideTimer = null
let current = null // 当前锚定的术语节点

const scopeText = computed(() =>
  entry.value ? SCOPE_LABEL[entry.value.scope] || '' : '',
)

function clearHide() {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

function hide(immediate = false) {
  clearHide()
  if (immediate) {
    visible.value = false
    pinned.value = false
    current = null
    return
  }
  hideTimer = setTimeout(() => {
    visible.value = false
    pinned.value = false
    current = null
  }, HIDE_DELAY)
}

/* 先渲染再量尺寸：浮层高度取决于文案长短，写死会在长条目上翻转错边。
 *
 * 用 nextTick 而不是 rAF —— 隐藏标签页里 rAF 不触发，这个 await 会
 * 永远停在这里（见 utils/glossaryScan.js 的同类问题）。 */
async function place(el) {
  const r = el.getBoundingClientRect()
  pos.value = { top: r.bottom + GAP, left: r.left, placement: 'bottom' }
  await nextTick()
  const box = tip.value?.getBoundingClientRect()
  if (!box) return

  let top = r.bottom + GAP
  let placement = 'bottom'
  // 下方放不下就翻到上方；上方也放不下时仍取下方（下方通常能滚动）
  if (top + box.height > window.innerHeight - EDGE && r.top - GAP - box.height > EDGE) {
    top = r.top - GAP - box.height
    placement = 'top'
  }

  let left = r.left
  const maxLeft = window.innerWidth - EDGE - box.width
  if (left > maxLeft) left = maxLeft
  if (left < EDGE) left = EDGE

  pos.value = { top, left, placement }
}

function show(el) {
  const key = el.dataset.term
  const e = GLOSSARY[key]
  if (!e) return
  clearHide()
  current = el
  entry.value = e
  visible.value = true
  place(el)
}

function onOver(ev) {
  const el = ev.target.closest?.('.gloss[data-term]')
  if (el) {
    if (el !== current) show(el)
    else clearHide()
    return
  }
  // 指针移到浮层上时不收
  if (ev.target.closest?.('.gloss-tip')) {
    clearHide()
    return
  }
  if (visible.value && !pinned.value) hide()
}

function onClick(ev) {
  const el = ev.target.closest?.('.gloss[data-term]')
  if (el) {
    ev.preventDefault()
    // 再点一次收起，触屏上这是唯一的关闭方式
    if (pinned.value && el === current) return hide(true)
    show(el)
    pinned.value = true
    return
  }
  if (!ev.target.closest?.('.gloss-tip')) hide(true)
}

function onFocus(ev) {
  const el = ev.target.closest?.('.gloss[data-term]')
  if (el) show(el)
}

function onBlur(ev) {
  if (ev.target.closest?.('.gloss[data-term]') && !pinned.value) hide()
}

function onKey(ev) {
  if (ev.key === 'Escape' && visible.value) hide(true)
}

/* 滚动时直接收起而不是跟随重算：控制台里表格容器各自滚动，
 * 跟随要监听每一个滚动祖先，收益不值这个复杂度。 */
function onScroll() {
  if (visible.value) hide(true)
}

onMounted(() => {
  // 观察 body 而不是 #app：对话框、下拉、抽屉都是 Teleport 到 body 的，
  // 只盯 #app 会让弹窗里的术语没有解释。
  startAutoScan(document.body)
  document.addEventListener('mouseover', onOver, true)
  document.addEventListener('click', onClick, true)
  document.addEventListener('focusin', onFocus, true)
  document.addEventListener('focusout', onBlur, true)
  document.addEventListener('keydown', onKey)
  window.addEventListener('scroll', onScroll, true)
  window.addEventListener('resize', onScroll)
})

onBeforeUnmount(() => {
  stopAutoScan()
  clearHide()
  document.removeEventListener('mouseover', onOver, true)
  document.removeEventListener('click', onClick, true)
  document.removeEventListener('focusin', onFocus, true)
  document.removeEventListener('focusout', onBlur, true)
  document.removeEventListener('keydown', onKey)
  window.removeEventListener('scroll', onScroll, true)
  window.removeEventListener('resize', onScroll)
})
</script>

<template>
  <Teleport to="body">
    <div
      v-show="visible && entry"
      ref="tip"
      class="gloss-tip"
      :class="`is-${pos.placement}`"
      :style="{ top: pos.top + 'px', left: pos.left + 'px' }"
      role="tooltip"
      data-no-term
    >
      <div class="gt-head">
        <span class="gt-term">{{ entry?.term }}</span>
        <span class="gt-scope" :class="entry?.scope">{{ scopeText }}</span>
      </div>
      <p class="gt-short">{{ entry?.short }}</p>
      <p v-if="entry?.detail" class="gt-detail">{{ entry.detail }}</p>
      <p v-if="entry?.value" class="gt-value">{{ entry.value }}</p>
    </div>
  </Teleport>
</template>

<style>
/* 术语相关的样式全部全局：扫描器注入的 span 不带 scoped 属性 */

/* 包装层必须是"透明"的——它插在原文本的位置上，不能改变换行、
   对齐、继承。display:contents 让它在布局上完全不存在。 */
.gloss-wrap {
  display: contents;
}
/* 原文本节点藏在这里，仍挂在文档中供 Vue 写入（见 glossaryScan.js）。
   用 display:none 而不是视觉隐藏，顺带让复制文本时不会出现两份。 */
.gloss-src {
  display: none;
}

.gloss {
  border-bottom: 0.0625rem dashed var(--line);
  cursor: help;
  transition: border-color 0.12s;
}
.gloss:hover,
.gloss:focus-visible {
  border-bottom-color: var(--amber);
  outline: none;
}
.gloss:focus-visible {
  border-radius: 0.125rem;
  box-shadow: 0 0 0 0.125rem rgb(240 173 61 / 35%);
}

.gloss-tip {
  position: fixed;
  z-index: 3000;
  max-width: 23rem;
  padding: 0.625rem 0.75rem;
  background: var(--panel2);
  border: var(--hair) solid var(--line);
  border-radius: var(--radius);
  box-shadow: 0 0.5rem 1.5rem rgb(0 0 0 / 45%);
  font-size: 0.8125rem;
  line-height: 1.6;
  color: var(--text);
  pointer-events: auto;
}

.gt-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.375rem;
}
.gt-term {
  font-weight: 600;
  color: var(--amber);
}
.gt-scope {
  padding: 0.0625rem 0.375rem;
  border-radius: 62.5rem;
  font-size: 0.6875rem;
  line-height: 1.5;
  white-space: nowrap;
}
.gt-scope.common {
  color: var(--blue);
  background: rgb(116 169 255 / 14%);
}
.gt-scope.local {
  color: var(--muted);
  background: rgb(142 154 170 / 14%);
}

.gloss-tip p {
  margin: 0;
}
.gt-short {
  color: var(--text);
}
.gt-detail {
  margin-top: 0.375rem !important;
  color: var(--muted);
}
.gt-value {
  margin-top: 0.5rem !important;
  padding-top: 0.375rem;
  border-top: var(--hair) solid var(--line2);
  font-family: var(--mono);
  font-size: 0.75rem;
  color: var(--green);
}
</style>
